import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type Language = "kk" | "ru" | "en";

interface LanguageSelectorProps {
  variant?: "desktop" | "mobile";
  className?: string;
}

const LanguageSelector = ({ variant = "desktop", className = "" }: LanguageSelectorProps) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: { code: Language; label: string }[] = [
    { code: "kk", label: "KZ" },
    { code: "ru", label: "RU" },
    { code: "en", label: "EN" },
  ];

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const changeLang = (lng: Language) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (variant === "mobile") {
    return (
      <div className={`flex flex-col gap-4 ${className}`}>
        <div className="text-red-900 text-base font-normal font-['Inter']">
          {t("header.languageLabel")}
        </div>
        <div className="flex items-center justify-around gap-4"> 
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => changeLang(lang.code)}
              className={`size-14 text-base font-medium font-['Inter'] transition-all ${
                i18n.language === lang.code
                  ? "bg-red-900 text-neutral-50"
                  : "bg-neutral-100 text-red-900 hover:bg-red-50"
              }`}
              aria-label={`Switch to ${lang.label}`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Desktop variant - Dropdown
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer flex items-center gap-2 text-red-900 text-lg font-medium font-['Inter'] tracking-tight hover:opacity-80 transition-opacity"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{currentLanguage.label}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full  right-0 mt-2 bg-white border border-neutral-200 rounded shadow-lg z-50">
          <div className="py-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => changeLang(lang.code)}
                className={`cursor-pointer w-full text-left px-4 py-2 text-base font-medium font-['Inter'] transition-colors ${
                  i18n.language === lang.code
                    ? "bg-red-900 text-neutral-50"
                    : "text-red-900 hover:bg-red-50"
                }`}
                aria-label={`Switch to ${lang.label}`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;

